import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiClock, FiCalendar, FiTag, FiShare2,
  FiBookmark, FiChevronUp, FiTwitter, FiLinkedin, FiCopy,
  FiCheckCircle, FiArrowRight, FiExternalLink, FiBookOpen,
} from 'react-icons/fi';
import { apiGetPost, apiGetAllPosts } from '../../api/blogApi';
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import cpp from 'react-syntax-highlighter/dist/esm/languages/prism/cpp';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import docker from 'react-syntax-highlighter/dist/esm/languages/prism/docker';
import InteractiveChart from '../../components/InteractiveChart';

SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('yml', yaml);
SyntaxHighlighter.registerLanguage('rust', rust);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('cpp', cpp);
SyntaxHighlighter.registerLanguage('c++', cpp);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('docker', docker);
SyntaxHighlighter.registerLanguage('dockerfile', docker);

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [progress, setProgress] = useState(0);
  const [showToc, setShowToc] = useState(true);
  const [activeSection, setActiveSection] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    apiGetPost(slug).then((res) => {
      if (!res.ok) { navigate('/blog'); return; }
      const p = res.data;
      setPost(p);
      setProgress(0);
      setActiveSection(0);

      /* Related posts: same category or overlapping tags */
      apiGetAllPosts({ status: 'published' }).then((allRes) => {
        if (!allRes.ok) return;
        const all = allRes.data.filter((x) => x.id !== p.id);
        const scored = all.map((x) => {
          let score = 0;
          if (x.category === p.category) score += 3;
          (x.tags || []).forEach((t) => { if ((p.tags || []).includes(t)) score += 1; });
          return { ...x, score };
        });
        scored.sort((a, b) => b.score - a.score);
        setRelated(scored.slice(0, 3));
      });
    });

    window.scrollTo(0, 0);
  }, [slug, navigate]);

  /* Reading progress + scroll spy */
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    setShowScrollTop(scrollTop > 600);

    if (!contentRef.current || !post?.sections) return;
    const headers = contentRef.current.querySelectorAll('.blog-section-heading');
    let current = 0;
    headers.forEach((h, i) => {
      if (h.getBoundingClientRect().top < 160) current = i;
    });
    setActiveSection(current);
  }, [post]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (idx) => {
    const headers = contentRef.current?.querySelectorAll('.blog-section-heading');
    if (headers?.[idx]) {
      headers[idx].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!post) return null;

  /* --- Helpers for rendering multimodal sections --- */
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vmMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}`;
    return null;
  };

  const renderInline = (text) => {
    if (!text) return null;
    const parts = [];
    let remaining = text;
    let key = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);
      const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
      let earliest = null;
      let type = null;
      [['bold', boldMatch], ['italic', italicMatch], ['link', linkMatch]].forEach(([t, m]) => {
        if (m && (earliest === null || m.index < earliest.index)) { earliest = m; type = t; }
      });
      if (!earliest) { parts.push(remaining); break; }
      if (earliest.index > 0) parts.push(remaining.slice(0, earliest.index));
      if (type === 'bold') parts.push(<strong key={key++}>{earliest[1]}</strong>);
      else if (type === 'italic') parts.push(<em key={key++}>{earliest[1]}</em>);
      else if (type === 'link') parts.push(<a key={key++} href={earliest[2]} target="_blank" rel="noopener noreferrer">{earliest[1]}</a>);
      remaining = remaining.slice(earliest.index + earliest[0].length);
    }
    return parts;
  };

  const renderTextContent = (content) => {
    if (!content) return null;
    return content.split('\n\n').map((para, j) => {
      if (para.startsWith('•') || para.startsWith('- ')) {
        const items = para.split('\n').filter(Boolean);
        return <ul key={j} className="blog-section-list">{items.map((item, k) => <li key={k}>{renderInline(item.replace(/^[•-]\s*/, ''))}</li>)}</ul>;
      }
      if (/^\d+\./.test(para)) {
        const items = para.split('\n').filter(Boolean);
        return <ol key={j} className="blog-section-list">{items.map((item, k) => <li key={k}>{renderInline(item.replace(/^\d+\.\s*/, ''))}</li>)}</ol>;
      }
      if (para.startsWith('>')) {
        return <blockquote key={j} className="ba-inline-quote">{renderInline(para.replace(/^>\s*/, ''))}</blockquote>;
      }
      return <p key={j}>{renderInline(para)}</p>;
    });
  };

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(post.title);

  return (
    <div className="blog-article-page">
      {/* Progress bar */}
      <div className="blog-progress-bar" style={{ width: `${progress}%` }} />

      {/* Sticky header */}
      <div className="blog-article-header">
        <div className="blog-article-header-inner">
          <Link to="/blog" className="blog-back"><FiArrowLeft /> Back to Blog</Link>
          <div className="blog-article-header-title">{post.title}</div>
          <div className="blog-article-header-actions">
            <button onClick={() => setShowToc(!showToc)} className="blog-toc-toggle">
              TOC
            </button>
            <button onClick={copyLink} title="Copy link">
              {copied ? <FiCheckCircle /> : <FiCopy />}
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="ba-hero">
        <div className="ba-hero-inner">
          <div className="ba-hero-text">
            <span className="ba-hero-category" style={{ '--cat-color': categoryColor(post.category) }}>
              {post.category}
            </span>
            <h1 className="ba-hero-title">{post.title}</h1>
            {post.excerpt && <p className="ba-hero-subtitle">{post.excerpt}</p>}
            <div className="ba-hero-meta">
              <div className="ba-hero-author">
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(post.author)}&background=0a1628&color=fff&size=40`}
                  alt={post.author}
                  className="ba-hero-avatar"
                />
                <div>
                  <span className="ba-hero-author-name">{post.author}</span>
                  {post.authorRole && <span className="ba-hero-author-role">{post.authorRole}</span>}
                </div>
              </div>
              <div className="ba-hero-details">
                <span><FiCalendar /> {formatDate(post.publishedDate)}</span>
                <span><FiClock /> {post.readTime} min read</span>
              </div>
            </div>
          </div>
          <div className="ba-hero-image-wrap">
            <img src={post.coverImage} alt={post.title} className="ba-hero-image" />
            <div className="ba-hero-image-glow" />
          </div>
        </div>
      </section>

      {/* Layout */}
      <div className="blog-article-layout">
        {/* Table of Contents */}
        {showToc && post.sections && post.sections.length > 0 && (
          <aside className="blog-article-toc">
            <div className="blog-toc-sticky">
              <h4>Table of Contents</h4>
              <ol>
                {post.sections.map((s, i) => (
                  <li key={i} className={activeSection === i ? 'active' : ''}>
                    <button onClick={() => scrollToSection(i)}>
                      {s.title || `Section ${i + 1}`}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        )}

        {/* Article Content */}
        <article className="blog-article-content" ref={contentRef}>
          {/* Sections */}
          {post.sections?.map((section, i) => (
            <div key={i} className="blog-article-section">
              {/* TEXT */}
              {(section.type === 'text' || !section.type) && (
                <>
                  <h2 className="blog-section-heading">{section.title}</h2>
                  <div className="blog-section-body">
                    {renderTextContent(section.content)}
                  </div>
                </>
              )}

              {/* IMAGE */}
              {section.type === 'image' && section.url && (
                <figure className="ba-image-section">
                  <img src={section.url} alt={section.alt || ''} loading="lazy" />
                  {section.caption && <figcaption>{section.caption}</figcaption>}
                </figure>
              )}

              {/* VIDEO */}
              {section.type === 'video' && getVideoEmbedUrl(section.url) && (
                <div className="ba-video-section">
                  <div className="ba-video-wrapper">
                    <iframe src={getVideoEmbedUrl(section.url)} title="Embedded video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen frameBorder="0" />
                  </div>
                  {section.caption && <p className="ba-video-caption">{section.caption}</p>}
                </div>
              )}

              {/* QUOTE */}
              {section.type === 'quote' && (
                <blockquote className="ba-quote-section">
                  <p>{section.content}</p>
                  {(section.attribution || section.source) && (
                    <footer>
                      {section.attribution && <cite>— {section.attribution}</cite>}
                      {section.source && <span>, {section.source}</span>}
                    </footer>
                  )}
                </blockquote>
              )}

              {/* CODE */}
              {section.type === 'code' && (
                <div className="ba-code-section">
                  {(section.caption || section.language) && (
                    <div className="ba-code-header">
                      {section.language && <span className="ba-code-lang">{section.language}</span>}
                      {section.caption && <span className="ba-code-caption">{section.caption}</span>}
                    </div>
                  )}
                  <SyntaxHighlighter
                    language={section.language || 'python'}
                    style={oneDark}
                    showLineNumbers
                    wrapLongLines
                    customStyle={{ margin: 0, borderRadius: '0 0 10px 10px', fontSize: '0.88rem' }}
                  >
                    {section.content}
                  </SyntaxHighlighter>
                </div>
              )}

              {/* CALLOUT */}
              {section.type === 'callout' && (
                <div className={`ba-callout-section ba-callout--${section.variant || 'info'}`}>
                  <span className="ba-callout-icon">
                    {section.variant === 'warning' ? '⚠️' : section.variant === 'tip' ? '🚀' : section.variant === 'note' ? '📝' : '💡'}
                  </span>
                  <div className="ba-callout-content">
                    {section.title && <strong>{section.title}</strong>}
                    <p>{section.content}</p>
                  </div>
                </div>
              )}

              {/* CITATION */}
              {section.type === 'citation' && (
                <div className="ba-citation-section">
                  <FiBookOpen className="ba-cite-icon" />
                  <div className="ba-cite-body">
                    <strong>{section.title}</strong>
                    <div className="ba-cite-meta">
                      {section.author && <span>{section.author}</span>}
                      {section.publication && <span>{section.publication}</span>}
                      {section.date && <span>{section.date}</span>}
                    </div>
                    {section.excerpt && <p className="ba-cite-excerpt">{section.excerpt}</p>}
                    {section.url && (
                      <a href={section.url} target="_blank" rel="noopener noreferrer" className="ba-cite-link">
                        <FiExternalLink /> View Source
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* DIVIDER */}
              {section.type === 'divider' && (
                <div className={`ba-divider-section ba-divider--${section.style || 'line'}`} />
              )}

              {/* CHART */}
              {section.type === 'chart' && section.chartConfig && (
                <div className="ba-chart-section">
                  {section.title && <h3 className="blog-section-heading">{section.title}</h3>}
                  <InteractiveChart chartConfig={section.chartConfig} caption={section.caption} />
                </div>
              )}

              {/* FIGURE WITH CHART */}
              {section.type === 'image' && section.chartConfig && (
                <div className="ba-chart-section">
                  {section.title && <h3 className="blog-section-heading">{section.title}</h3>}
                  <InteractiveChart chartConfig={section.chartConfig} caption={section.caption} />
                </div>
              )}
            </div>
          ))}

          {/* Tags */}
          <div className="blog-article-tags">
            {post.tags.map((t) => (
              <Link key={t} to={`/blog?tag=${t}`} className="blog-article-tag">
                <FiTag /> {t}
              </Link>
            ))}
          </div>

          {/* Share bar */}
          <div className="blog-article-share">
            <span>Share this article:</span>
            <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`} target="_blank" rel="noopener noreferrer" title="Twitter">
              <FiTwitter />
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" title="LinkedIn">
              <FiLinkedin />
            </a>
            <button onClick={copyLink} title="Copy link">
              {copied ? <FiCheckCircle /> : <FiCopy />}
            </button>
            <button title="Save"><FiBookmark /></button>
            <button title="Share" onClick={() => navigator.share?.({ title: post.title, url: window.location.href }).catch(() => {})}><FiShare2 /></button>
          </div>

          {/* Author card */}
          <div className="blog-author-card">
            <div className="blog-author-avatar">
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(post.author)}&background=0a1628&color=fff&size=80`}
                alt={post.author}
              />
            </div>
            <div className="blog-author-info">
              <h4>{post.author}</h4>
              {post.authorRole && <span className="blog-author-role">{post.authorRole}</span>}
              <p>Building production AI systems at Vector Labs. Charlotte, NC.</p>
            </div>
          </div>

          {/* CTA */}
          <div className="blog-article-cta">
            <h3>Building in public. Ship with us.</h3>
            <p>Our products are in beta — get early access and help shape what we build next.</p>
            <Link to="/products" className="blog-cta-btn">
              Explore Products <FiArrowRight />
            </Link>
          </div>

          {/* Related posts */}
          {related.length > 0 && (
            <div className="blog-related">
              <h3>Related Articles</h3>
              <div className="blog-related-grid">
                {related.map((r) => (
                  <Link to={`/blog/${r.slug}`} key={r.id} className="blog-related-card">
                    <img src={r.coverImage} alt={r.title} loading="lazy" />
                    <div className="blog-related-body">
                      <span className="blog-related-cat" style={{ color: categoryColor(r.category) }}>{r.category}</span>
                      <h4>{r.title}</h4>
                      <span className="blog-related-meta"><FiClock /> {r.readTime} min</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button className="blog-scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <FiChevronUp />
        </button>
      )}
    </div>
  );
}

/* Helpers */
function formatDate(dateStr) {
  if (!dateStr) return 'Unpublished';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function categoryColor(cat) {
  const colors = {
    'Engineering': '#6cb4ff',
    'AI & Automation': '#a78bfa',
    'Company Updates': '#34d399',
    'Case Studies': '#fbbf24',
    'Industry Insights': '#f87171',
    'Product Launches': '#22d3ee',
    'Tutorials & Guides': '#818cf8',
  };
  return colors[cat] || '#94a3b8';
}
